import React, { useState, useCallback, useEffect, useContext } from 'react';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Delete as DeleteIcon,
  GetApp as ExportIcon,
  PhotoCamera as PhotoIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import axios from 'axios';
import { ReportContext } from '../../Context';
import { UserContext } from '../../Context';
import { ThemeContext } from '../../Context';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../../utils/logClient';

function PhotoDoc() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [images, setImages] = useState([]);
  const [editingImage, setEditingImage] = useState(null);
  const [imagesPerPage, setImagesPerPage] = useState(2);
  const [isExporting, setIsExporting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);

  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Charger les données du rapport et les paramètres
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupérer les paramètres
        const settingsResponse = await axios.get('/api/settings');
        setSettings(settingsResponse.data);

        // Récupérer les données du rapport si disponible
        if (report) {
          const token = sessionStorage.getItem('authToken');
          const reportResponse = await axios.get(`/api/report/search`, {
            params: { number: report.number, year: report.year },
            headers: { Authorization: `Bearer ${token}` },
          });
          setReportData(reportResponse.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    };

    fetchData();
  }, [report]);

  // Fonction pour construire le numéro de PV complet
  const buildReportNumber = () => {
    if (!reportData || !settings) return 'XXXXXX';
    
    const shortJudicialDistrict = settings.shortJudicialDistrict || '__';
    const indice = reportData.indice || '__';
    const judicialDivision = settings.judicialDivision || '__';
    const number = report?.number || '__';
    const year = report?.year || '__';
    
    return `${shortJudicialDistrict}.${indice}.${judicialDivision}.${number}/${year}`;
  };

  // Fonction pour ajouter des images
  const handleAddImages = useCallback((event) => {
    const files = Array.from(event.target.files);
    
    files.forEach((file) => {
      // Vérification de sécurité - seulement les images
      if (!file.type.startsWith('image/')) {
        setAlert({ type: 'error', message: `${file.name} n'est pas une image valide` });
        return;
      }

      // Limite de taille (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setAlert({ type: 'error', message: `${file.name} est trop volumineux (max 10MB)` });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          file: file,
          dataUrl: e.target.result,
          title: file.name.split('.')[0],
          originalName: file.name,
          orientation: 'auto' // Orientation par défaut : automatique
        };
        
        setImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    event.target.value = '';
  }, []);

  // Fonction pour déplacer une image
  const moveImage = useCallback((index, direction) => {
    setImages(prev => {
      const newImages = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (newIndex >= 0 && newIndex < newImages.length) {
        [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      }
      
      return newImages;
    });
  }, []);

  // Fonction pour supprimer une image
  const removeImage = useCallback((id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  }, []);

  // Fonction pour éditer une image
  const handleEditImage = useCallback((image) => {
    setEditingImage({ ...image });
  }, []);

  // Fonction pour sauvegarder les modifications
  const saveImageEdit = useCallback(() => {
    setImages(prev => prev.map(img =>
      img.id === editingImage.id ? editingImage : img
    ));
    setEditingImage(null);
  }, [editingImage]);

  // Fonction pour changer l'orientation d'une image
  const changeImageOrientation = useCallback((id, orientation) => {
    setImages(prev => prev.map(img =>
      img.id === id ? { ...img, orientation } : img
    ));
  }, []);

  // Fonction pour charger une image dans le canvas
  const loadImageToCanvas = (dataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = dataUrl;
    });
  };

  // Fonction pour faire pivoter une image de 90 degrés
  const rotateImage90 = (img) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Échanger largeur et hauteur pour la rotation
    canvas.width = img.height;
    canvas.height = img.width;

    // Translater et faire la rotation
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    return canvas.toDataURL('image/jpeg', 0.9);
  };

  // Fonction pour déterminer si une image doit être tournée selon son orientation choisie
  const shouldRotateImage = (img, imageOrientation, imagesPerPage) => {
    const isHorizontal = img.width > img.height;

    switch (imageOrientation) {
      case 'vertical':
        return isHorizontal; // Tourner si elle est horizontale pour la rendre verticale
      case 'horizontal':
        return !isHorizontal; // Tourner si elle est verticale pour la rendre horizontale
      case 'auto':
      default:
        // Logique automatique : maximiser l'espace utilisé
        const usableWidth = 210 - 20; // A4 width - margins
        const usableHeight = 297 - 30; // A4 height - margins - header

        if (imagesPerPage === 1) {
          // Une image par page : utiliser tout l'espace disponible
          const ratioNormal = Math.min(usableWidth / img.width, usableHeight / img.height);
          const ratioRotated = Math.min(usableWidth / img.height, usableHeight / img.width);

          // Choisir l'orientation qui donne le plus gros ratio (moins de blanc)
          return ratioRotated > ratioNormal;

        } else if (imagesPerPage === 2) {
          // Deux images par page : une au-dessus de l'autre
          const halfHeight = usableHeight / 2 - 5;

          const ratioNormal = Math.min(usableWidth / img.width, halfHeight / img.height);
          const ratioRotated = Math.min(usableWidth / img.height, halfHeight / img.width);

          // Choisir l'orientation qui maximise l'espace
          return ratioRotated > ratioNormal;

        } else { // 4 images par page
          const quarterWidth = usableWidth / 2 - 5;
          const quarterHeight = usableHeight / 2 - 5;

          const ratioNormal = Math.min(quarterWidth / img.width, quarterHeight / img.height);
          const ratioRotated = Math.min(quarterWidth / img.height, quarterHeight / img.width);

          // Choisir l'orientation qui maximise l'espace
          return ratioRotated > ratioNormal;
        }
    }
  };

  // Fonction pour exporter en PDF
  const exportToPDF = useCallback(async () => {
    if (images.length === 0) {
      setAlert({ type: 'warning', message: 'Aucune image à exporter' });
      return;
    }

    setIsExporting(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 15; // Espace réservé pour l'en-tête
      const usableWidth = pageWidth - (2 * margin);
      const usableHeight = pageHeight - (2 * margin) - headerHeight;

      let currentPage = 1;
      let imagesOnCurrentPage = 0;

      // Fonction pour ajouter l'en-tête de page
      const addPageHeader = (pageNumber) => {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        const headerText = `Annexe n° _____ au procès-verbal ${buildReportNumber()}`;
        const textWidth = pdf.getTextWidth(headerText);
        const rightX = pageWidth - margin - textWidth;
        pdf.text(headerText, rightX, margin + 8);
      };

      // Ajouter l'en-tête de la première page
      addPageHeader(currentPage);

      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        
        // Nouvelle page si nécessaire
        if (imagesOnCurrentPage >= imagesPerPage && i > 0) {
          pdf.addPage();
          currentPage++;
          addPageHeader(currentPage); // Ajouter l'en-tête sur la nouvelle page
          imagesOnCurrentPage = 0;
        }

        // Charger l'image
        const img = await loadImageToCanvas(image.dataUrl);

        // Variables pour l'image finale
        let finalImageData = image.dataUrl;
        let finalWidth = img.width;
        let finalHeight = img.height;

        // Déterminer si l'image doit être tournée selon l'orientation choisie
        const shouldRotate = shouldRotateImage(img, image.orientation, imagesPerPage);
        
        // Appliquer la rotation si nécessaire
        if (shouldRotate) {
          finalImageData = rotateImage90(img);
          finalWidth = img.height;
          finalHeight = img.width;
        }

        // Calculer les dimensions et la position selon le nombre d'images par page
        let imgWidth, imgHeight, x, y;

        if (imagesPerPage === 1) {
          // Une image par page - utiliser tout l'espace disponible
          const ratio = Math.min(usableWidth / finalWidth, usableHeight / finalHeight);
          imgWidth = finalWidth * ratio;
          imgHeight = finalHeight * ratio;
          x = margin + (usableWidth - imgWidth) / 2;
          y = margin + headerHeight + (usableHeight - imgHeight) / 2;

        } else if (imagesPerPage === 2) {
          // Deux images par page - une au-dessus de l'autre
          const halfHeight = usableHeight / 2 - 5;
          const ratio = Math.min(usableWidth / finalWidth, halfHeight / finalHeight);
          imgWidth = finalWidth * ratio;
          imgHeight = finalHeight * ratio;
          x = margin + (usableWidth - imgWidth) / 2;
          y = margin + headerHeight + (imagesOnCurrentPage % 2) * (halfHeight + 10) + (halfHeight - imgHeight) / 2;

        } else { // 4 images par page
          const quarterWidth = usableWidth / 2 - 5;
          const quarterHeight = usableHeight / 2 - 5;
          const ratio = Math.min(quarterWidth / finalWidth, quarterHeight / finalHeight);
          imgWidth = finalWidth * ratio;
          imgHeight = finalHeight * ratio;

          const col = imagesOnCurrentPage % 2;
          const row = Math.floor(imagesOnCurrentPage / 2);
          x = margin + col * (quarterWidth + 10) + (quarterWidth - imgWidth) / 2;
          y = margin + headerHeight + row * (quarterHeight + 10) + (quarterHeight - imgHeight) / 2;
        }

        // Ajouter l'image au PDF
        pdf.addImage(finalImageData, 'JPEG', x, y, imgWidth, imgHeight);

        // Ajouter le titre numéroté
        if (image.title) {
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          const imageNumber = i + 1; // Numéro séquentiel basé sur l'index
          const numberedTitle = `${imageNumber} : ${image.title}`;
          const textY = y + imgHeight + 5;
          pdf.text(numberedTitle, x, textY);
        }

        imagesOnCurrentPage++;
      }

      // Sauvegarder le PDF avec timestamp en heure locale
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      pdf.save(`dossier-photographique-${timestamp}.pdf`);
      
      // Log de la création du dossier photographique
      logAction(LOG_ACTIONS.ANNEXE_PHOTO_DOC, formatReportNumber(report?.number, report?.year), {
        imagesCount: images.length
      });
      
      setAlert({ type: 'success', message: 'PDF exporté avec succès!' });
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      setAlert({ type: 'error', message: 'Erreur lors de l\'export du PDF' });
    } finally {
      setIsExporting(false);
    }
  }, [images, imagesPerPage]);

  return (
    <Box sx={{ 
      maxWidth: '100%', 
      height: '100%', 
      p: 0,
      backgroundColor: isDark ? '#1a252f' : 'inherit',
      minHeight: '100%'
    }}>
      {/* Message d'information important */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 3,
          backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : undefined,
          color: isDark ? '#4fc3f7' : undefined,
          '& .MuiAlert-icon': {
            color: isDark ? '#4fc3f7' : undefined
          }
        }}
      >
        Attention, les images importées ne sont pas enregistrées. Si vous quittez ou actualisez la page, vos selections disparaîtront.
      </Alert>

      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ 
            mb: 2,
            backgroundColor: isDark 
              ? (alert.type === 'success' 
                  ? 'rgba(76, 175, 80, 0.15)' 
                  : alert.type === 'error'
                    ? 'rgba(244, 67, 54, 0.15)'
                    : 'rgba(255, 193, 7, 0.15)')
              : undefined,
            color: isDark 
              ? (alert.type === 'success' 
                  ? '#4ecdc4' 
                  : alert.type === 'error'
                    ? '#ff6b6b'
                    : '#f39c12')
              : undefined,
            '& .MuiAlert-icon': {
              color: isDark 
                ? (alert.type === 'success' 
                    ? '#4ecdc4' 
                    : alert.type === 'error'
                      ? '#ff6b6b'
                      : '#f39c12')
                : undefined
            }
          }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Boutons d'action et paramètres */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          component="label"
          startIcon={<AddIcon />}
          color="primary"
          sx={{
            backgroundColor: isDark ? '#4fc3f7' : undefined,
            color: isDark ? '#000' : undefined,
            '&:hover': {
              backgroundColor: isDark ? '#29b6f6' : undefined
            }
          }}
        >
          Ajouter des photos
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            onChange={handleAddImages}
          />
        </Button>

        <Chip 
          label={`${images.length} photo(s)`} 
          color="primary" 
          variant="outlined"
          sx={{
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : undefined,
            borderColor: isDark ? '#4fc3f7' : undefined,
            color: isDark ? '#4fc3f7' : undefined
          }}
        />

        <FormControl size="small" sx={{ 
          minWidth: 160,
          '& .MuiInputLabel-root': {
            color: isDark ? '#bdc3c7' : undefined,
            '&.Mui-focused': {
              color: isDark ? '#4fc3f7' : undefined
            }
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#22303e' : undefined,
            color: isDark ? '#ecf0f1' : undefined,
            '& fieldset': {
              borderColor: isDark ? '#2d3f4c' : undefined
            },
            '&:hover fieldset': {
              borderColor: isDark ? '#4fc3f7' : undefined
            },
            '&.Mui-focused fieldset': {
              borderColor: isDark ? '#4fc3f7' : undefined
            }
          },
          '& .MuiSvgIcon-root': {
            color: isDark ? '#ecf0f1' : undefined
          }
        }}>
          <InputLabel>Images par page</InputLabel>
          <Select
            value={imagesPerPage}
            label="Images par page"
            onChange={(e) => setImagesPerPage(e.target.value)}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: isDark ? '#22303e' : undefined,
                  color: isDark ? '#ecf0f1' : undefined
                }
              }
            }}
          >
            <MenuItem 
              value={1}
              sx={{
                backgroundColor: isDark ? '#22303e' : undefined,
                color: isDark ? '#ecf0f1' : undefined,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                }
              }}
            >
              1 image
            </MenuItem>
            <MenuItem 
              value={2}
              sx={{
                backgroundColor: isDark ? '#22303e' : undefined,
                color: isDark ? '#ecf0f1' : undefined,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                }
              }}
            >
              2 images
            </MenuItem>
            <MenuItem 
              value={4}
              sx={{
                backgroundColor: isDark ? '#22303e' : undefined,
                color: isDark ? '#ecf0f1' : undefined,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                }
              }}
            >
              4 images
            </MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={isExporting ? <CircularProgress size={20} /> : <ExportIcon />}
          onClick={exportToPDF}
          disabled={images.length === 0 || isExporting}
          sx={{
            borderColor: isDark ? '#4fc3f7' : undefined,
            color: isDark ? '#4fc3f7' : undefined,
            '&:hover': {
              borderColor: isDark ? '#29b6f6' : undefined,
              backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
            },
            '&.Mui-disabled': {
              borderColor: isDark ? '#2d3f4c' : undefined,
              color: isDark ? '#7f8c8d' : undefined
            }
          }}
        >
          {isExporting ? 'Export en cours...' : 'Exporter en PDF'}
        </Button>

      </Box>

      {/* Liste des images */}
      <Grid container spacing={2} sx={{
        backgroundColor: isDark ? '#1a252f' : 'inherit'
      }}>
        {images.map((image, index) => (
          <Grid item xs={12} key={image.id}>
            <Card sx={{ 
              display: 'flex', 
              minHeight: 120,
              backgroundColor: isDark ? '#22303e' : undefined,
              color: isDark ? '#ecf0f1' : undefined
            }}>
              <CardMedia
                component="img"
                sx={{ width: 120, height: 120, objectFit: 'cover', flexShrink: 0 }}
                image={image.dataUrl}
                alt={image.title}
              />
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography variant="h6" noWrap sx={{
                  mb: 1,
                  color: isDark ? '#ecf0f1' : 'inherit'
                }}>
                  {image.title || 'Sans titre'}
                </Typography>
                <Typography variant="caption" sx={{
                  color: isDark ? '#95a5a6' : 'text.secondary',
                  mb: 1
                }}>
                  Fichier: {image.originalName.length > 20 ? image.originalName.substring(0, 20) + '...' : image.originalName}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
                  <FormControl size="small" sx={{
                    width: '100%',
                    maxWidth: 120,
                    '& .MuiInputLabel-root': {
                      color: isDark ? '#bdc3c7' : undefined,
                      fontSize: '0.75rem',
                      '&.Mui-focused': {
                        color: isDark ? '#4fc3f7' : undefined
                      }
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: isDark ? '#1a252f' : undefined,
                      color: isDark ? '#ecf0f1' : undefined,
                      fontSize: '0.75rem',
                      '& fieldset': {
                        borderColor: isDark ? '#2d3f4c' : undefined
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#4fc3f7' : undefined
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDark ? '#4fc3f7' : undefined
                      }
                    },
                    '& .MuiSvgIcon-root': {
                      color: isDark ? '#ecf0f1' : undefined,
                      fontSize: '1rem'
                    }
                  }}>
                    <InputLabel>Orientation</InputLabel>
                    <Select
                      value={image.orientation}
                      label="Orientation"
                      onChange={(e) => changeImageOrientation(image.id, e.target.value)}
                      sx={{ fontSize: '0.75rem' }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: isDark ? '#22303e' : undefined,
                            color: isDark ? '#ecf0f1' : undefined
                          }
                        }
                      }}
                    >
                      <MenuItem
                        value="auto"
                        sx={{
                          backgroundColor: isDark ? '#22303e' : undefined,
                          color: isDark ? '#ecf0f1' : undefined,
                          fontSize: '0.75rem',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                          }
                        }}
                      >
                        Automatique
                      </MenuItem>
                      <MenuItem
                        value="vertical"
                        sx={{
                          backgroundColor: isDark ? '#22303e' : undefined,
                          color: isDark ? '#ecf0f1' : undefined,
                          fontSize: '0.75rem',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                          }
                        }}
                      >
                        Vertical
                      </MenuItem>
                      <MenuItem
                        value="horizontal"
                        sx={{
                          backgroundColor: isDark ? '#22303e' : undefined,
                          color: isDark ? '#ecf0f1' : undefined,
                          fontSize: '0.75rem',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.1)' : undefined
                          }
                        }}
                      >
                        Horizontal
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, flexShrink: 0 }}>
                <IconButton
                  size="small"
                  onClick={() => moveImage(index, 'up')}
                  disabled={index === 0}
                  sx={{
                    color: isDark ? '#ecf0f1' : undefined,
                    '&.Mui-disabled': {
                      color: isDark ? '#7f8c8d' : undefined
                    }
                  }}
                >
                  <ArrowUpIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveImage(index, 'down')}
                  disabled={index === images.length - 1}
                  sx={{
                    color: isDark ? '#ecf0f1' : undefined,
                    '&.Mui-disabled': {
                      color: isDark ? '#7f8c8d' : undefined
                    }
                  }}
                >
                  <ArrowDownIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleEditImage(image)}
                  color="primary"
                  sx={{
                    color: isDark ? '#4fc3f7' : undefined
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => removeImage(image.id)}
                  color="error"
                  sx={{
                    color: isDark ? '#ff6b6b' : undefined
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {images.length === 0 && (
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center', 
          mt: 3,
          backgroundColor: isDark ? '#22303e' : undefined
        }}>
          <PhotoIcon sx={{ 
            fontSize: 48, 
            color: isDark ? '#95a5a6' : 'text.secondary', 
            mb: 2 
          }} />
          <Typography variant="h6" sx={{
            color: isDark ? '#95a5a6' : 'text.secondary'
          }}>
            Aucune photo ajoutée
          </Typography>
          <Typography variant="body2" sx={{
            color: isDark ? '#95a5a6' : 'text.secondary'
          }}>
            Cliquez sur "Ajouter des photos" pour commencer
          </Typography>
        </Paper>
      )}

      {/* Dialog d'édition */}
      <Dialog 
        open={!!editingImage} 
        onClose={() => setEditingImage(null)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#22303e' : undefined,
            color: isDark ? '#ecf0f1' : undefined
          }
        }}
      >
        <DialogTitle sx={{
          color: isDark ? '#ecf0f1' : 'inherit'
        }}>
          Modifier la photo
        </DialogTitle>
        <DialogContent>
          {editingImage && (
            <Box sx={{ pt: 1 }}>
              <img
                src={editingImage.dataUrl}
                alt="Aperçu"
                style={{ width: '100%', maxHeight: 200, objectFit: 'contain', marginBottom: 16 }}
              />
              <TextField
                fullWidth
                label="Titre"
                value={editingImage.title}
                onChange={(e) => setEditingImage(prev => ({ ...prev, title: e.target.value }))}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: isDark ? '#1a252f' : 'inherit',
                    color: isDark ? '#ecf0f1' : 'inherit',
                    '& fieldset': {
                      borderColor: isDark ? '#2d3f4c' : undefined
                    },
                    '&:hover fieldset': {
                      borderColor: isDark ? '#4fc3f7' : undefined
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDark ? '#4fc3f7' : undefined
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: isDark ? '#bdc3c7' : undefined,
                    '&.Mui-focused': {
                      color: isDark ? '#4fc3f7' : undefined
                    }
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditingImage(null)}
            sx={{
              color: isDark ? '#bdc3c7' : undefined
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={saveImageEdit} 
            variant="contained"
            sx={{
              backgroundColor: isDark ? '#4fc3f7' : undefined,
              color: isDark ? '#000' : undefined,
              '&:hover': {
                backgroundColor: isDark ? '#29b6f6' : undefined
              }
            }}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PhotoDoc;



